
import { createContext, useContext, useEffect, useState } from 'react';
import { Session, User } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { useNavigate } from 'react-router-dom';
import { toast } from '@/components/ui/use-toast';

type AuthContextType = {
  user: User | null;
  session: Session | null;
  isLoading: boolean;
  signOut: () => Promise<void>;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Utility function to clean up auth state
const cleanupAuthState = () => {
  // Remove standard auth tokens
  localStorage.removeItem('supabase.auth.token');
  
  // Remove all Supabase auth keys from localStorage
  Object.keys(localStorage).forEach((key) => {
    if (key.startsWith('supabase.auth.') || key.includes('sb-')) {
      localStorage.removeItem(key);
    }
  });
  
  // Remove from sessionStorage if in use
  Object.keys(sessionStorage || {}).forEach((key) => {
    if (key.startsWith('supabase.auth.') || key.includes('sb-')) {
      sessionStorage.removeItem(key);
    }
  });
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);
  // Track authentication stability to prevent redirect loops
  const [isAuthStable, setIsAuthStable] = useState(false);
  const navigate = useNavigate();

  // Initialize auth state once on mount
  useEffect(() => {
    console.log("Auth provider initialized");
    setIsLoading(true);
    let authTimeout: ReturnType<typeof setTimeout>;
    
    // Set up auth state listener first
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, currentSession) => {
      console.log('Auth state changed:', event, currentSession?.user?.email);
      
      // Update state
      setSession(currentSession);
      setUser(currentSession?.user ?? null);
      
      if (event === 'SIGNED_IN') {
        console.log("User signed in event received");
        // Delay notifications until after initialization
        if (isInitialized) {
          toast({
            title: "Welcome back!",
            description: "You have successfully signed in.",
          });
        }
      }
      
      if (event === 'SIGNED_OUT') {
        console.log("User signed out event received");
        // Only show toast if we're initialized (prevents toast on initial page load)
        if (isInitialized) {
          toast({
            title: "Signed out",
            description: "You have been signed out successfully.",
          });
        }
      }
    });

    // After setting up listener, check initial session
    const initializeAuth = async () => {
      try {
        console.log("Checking for existing session...");
        const { data: { session: initialSession }, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error("Error fetching initial session:", error);
          throw error;
        }
        
        console.log("Initial session check:", initialSession?.user?.email || "No session");
        
        // Set user and session state
        setSession(initialSession);
        setUser(initialSession?.user ?? null);
        
        // Wait briefly to ensure session state is stable before allowing redirects
        // This helps prevent redirect loops in the editor environment
        authTimeout = setTimeout(() => {
          setIsAuthStable(true);
          console.log("Auth state stabilized, safe to perform redirects now");
        }, 500); // Increased from 300ms to 500ms for more stability
      } catch (error) {
        console.error("Error during auth initialization:", error);
        // Even in case of errors, set auth as stable after some delay
        // to prevent hanging in loading state
        setTimeout(() => {
          setIsAuthStable(true);
          setIsLoading(false);
        }, 500);
      } finally {
        setIsLoading(false);
        setIsInitialized(true);
      }
    };

    initializeAuth();

    return () => {
      subscription.unsubscribe();
      clearTimeout(authTimeout);
    };
  }, []);

  const signOut = async () => {
    try {
      setIsLoading(true);
      setIsAuthStable(false); // Reset auth stability during sign-out process
      
      console.log("Starting sign out process");
      
      // Clear user and session state first
      setUser(null);
      setSession(null);
      
      // Clean up existing auth state
      cleanupAuthState();
      
      // Attempt global sign out
      await supabase.auth.signOut({ scope: 'global' });
      console.log("Supabase sign out completed");
      
      // Add a delay before navigating to ensure clean state
      setTimeout(() => {
        navigate('/', { replace: true });
      }, 300);
    } catch (err) {
      console.error("Error during sign out:", err);
      
      // Attempt navigation even if sign-out fails
      setTimeout(() => {
        navigate('/');
      }, 300);
    } finally {
      setTimeout(() => {
        setIsLoading(false);
        setIsAuthStable(true);
      }, 300);
    }
  };

  return (
    <AuthContext.Provider value={{ user, session, isLoading, signOut }}>
      {isInitialized ? children : (
        <div className="flex h-screen items-center justify-center bg-background">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-gold border-t-transparent"></div>
        </div>
      )}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
